import { format } from 'date-fns'

import { Market } from '../../../../features/markets/types/Market'
import { Store } from '../../../../features/store/types/Store'
import { AnalysisListRow, AnalyzedVersionPerMarket, GameAnalysis, GameVersion, latestGameVersion } from '../types/GameAnalyses'
import { GamePosition, InternalGameDocument } from '../types/InternalGame'

const systemUser = 'system@gamerefinery.com'
const defaultTargetMarketIso = 'us'

export type AnalysisPosition = GamePosition & { marketPublished: boolean }

// Legacy's `positionService.createFromArray`: the game's stores × markets in the game's order, named from the listed ones.
export const getAnalysisPositions = (game: Pick<InternalGameDocument, 'stores' | 'markets'>, stores: Store[], markets: Market[]): AnalysisPosition[] =>
  (game.stores ?? []).flatMap((storeRef) => {
    const store = stores.find(({ id }) => id === storeRef.id)
    if (!store) return []
    return (game.markets ?? []).flatMap((marketRef) => {
      const market = markets.find(({ id }) => id === marketRef.id)
      if (!market) return []
      return [
        {
          id: `${store.id}-${market.id}`,
          storeId: store.id,
          storeName: store.name,
          marketId: market.id,
          marketIso: market.iso,
          marketName: market.name,
          marketPublished: !!market.published,
          fullName: `${store.name} ${market.name}`,
        },
      ]
    })
  })

// Every store × market, for naming an analysis' position whatever the game's positions are now.
export const getAllPositions = (stores: Store[], markets: Market[]) => getAnalysisPositions({ stores, markets }, stores, markets)

export const getPositionOptions = (positions: AnalysisPosition[], selected?: AnalysisPosition) =>
  positions.filter((position) => position.marketPublished || position.id === selected?.id)

export const getDefaultSourcePosition = (positions: AnalysisPosition[], settingMarketIso: string | null) =>
  positions.find(({ marketIso }) => !!settingMarketIso && marketIso === settingMarketIso) ?? positions[0]

export const getDefaultTargetPosition = (positions: AnalysisPosition[]) =>
  positions.find(({ marketIso }) => marketIso === defaultTargetMarketIso) ?? positions[0]

export const isSystemAnalysis = (analysis: Pick<GameAnalysis, 'createdBy'>) => analysis.createdBy === systemUser

// Legacy's `head(sortBy(analyses, 'ts').reverse())`.
export const getLatestAnalysisId = (analyses: GameAnalysis[]) =>
  analyses.reduce<GameAnalysis | undefined>((latest, analysis) => (!latest || analysis.ts >= latest.ts ? analysis : latest), undefined)?.id

const uniqueVersions = (versions: GameVersion[]) => Array.from(new Set(versions.map(({ version }) => version)))

export const getAnalysisListRows = (analyses: GameAnalysis[], versions: GameVersion[]): AnalysisListRow[] => {
  const analyzed = new Set(analyses.map(({ gameVersion }) => gameVersion))
  const unanalyzed = uniqueVersions(versions)
    .filter((name) => !analyzed.has(name))
    .map((name) => ({ id: `version-${name}`, gameVersion: name, ts: versions.find(({ version }) => version === name)?.versionReleaseDate ?? 0 }))
  const rows = [...analyses.map((analysis) => ({ id: analysis.id, ts: analysis.ts, gameVersion: analysis.gameVersion, analysis })), ...unanalyzed]
  return rows.sort((a, b) => b.ts - a.ts)
}

export const getCloneVersionOptions = (versions: GameVersion[]) => [latestGameVersion, ...uniqueVersions(versions)]

// Legacy's badges clone from the market whose analyzed version comes first in the (newest first) version list.
export const getBadgeCloneSourceIso = (versions: GameVersion[], perMarket: AnalyzedVersionPerMarket[]) => {
  for (const { version } of versions) {
    const source = perMarket.find(({ gameVersion }) => gameVersion === version)
    if (source) return source.marketIso
  }
  return undefined
}

export const getAnalysisPowerScore = (analysis: Pick<GameAnalysis, 'powerScore'>) =>
  typeof analysis.powerScore === 'number' ? Math.round(analysis.powerScore) : undefined

export const formatAnalysisListDate = (timestamp: number) => format(timestamp, 'MMM d, yyyy H:mm')
export const formatAnalysisInfoDate = (timestamp: number) => format(timestamp, 'dd.MM.yyyy HH:mm')

export type AnalysisInfoAction = 'created' | 'modified' | 'finalized'
export type AnalysisInfoLine = { action: AnalysisInfoAction; at: string; by: string; isSystem: boolean }

const infoFields: { action: AnalysisInfoAction; at: keyof GameAnalysis; by: keyof GameAnalysis }[] = [
  { action: 'created', at: 'createdAt', by: 'createdBy' },
  { action: 'modified', at: 'modifiedAt', by: 'modifiedBy' },
  { action: 'finalized', at: 'finalizedAt', by: 'finalizedBy' },
]

export const getAnalysisInfoLines = (analysis: GameAnalysis): AnalysisInfoLine[] =>
  infoFields.flatMap(({ action, at, by }) => {
    const timestamp = analysis[at] as number | null | undefined
    const user = analysis[by] as string | null | undefined
    return timestamp && user ? [{ action, at: formatAnalysisInfoDate(timestamp), by: user, isSystem: user === systemUser }] : []
  })
